import React, { useContext, useState } from 'react';
import { t } from 'Src/i18n';
import { Modal } from 'Components/frameworks/modal/Modal';
import { RadioGroup } from 'Components/frameworks/formik/RadioGroup';
import { Formik } from 'formik';
import { AFTERNOON, MORNING } from 'Components/widgets/reservation-block/reservationBlock.utils';
import { Button } from 'Components/frameworks/button/Button';
import { ChildcareContext } from 'Context/ChildcareContext';
type Props = {
  onClose: () => void;
  headerLabel: string;
  service: PricingInfoWithScheduleType;
  slots: ReadonlyArray<TimeSlotType>;
};

/**
 This PopInReservationBlockMorningAfternoonExclusive covers one specific case of a childcare that is only available to book :
  - the morning
  - or in the afternoon
  - or all day
 in an exclusive way (only ONE) and for the WHOLE Stay
 *
 */
export const getSlotLabel = (slotType: string): string => {
  switch (slotType) {
    case MORNING:
      return t('booking.activities.timeSlot.morningTitle');

    case AFTERNOON:
      return t('booking.activities.timeSlot.afternoonTitle');

    case 'DAY':
      return t('booking.activities.timeSlot.allDayTitle');

    default:
      return t('booking.activities.timeSlot.allDayTitle');
  }
};
export const PopInReservationBlockMorningAfternoonExclusive = ({
  slots,
  onClose,
  headerLabel,
  service,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addAttendeeChildcare } = useContext(ChildcareContext);
  const { attendees: pricingInfoAttendees } = service;
  const currentChildPricingInfo = pricingInfoAttendees.find(
    (attendee) => attendee.id === service.childId,
  );
  // as we only have one schedule we take the first one
  const currentChildSchedule = currentChildPricingInfo?.schedules?.[0];
  const initiallySelectedSlot = currentChildSchedule?.timeSlots?.find(
    ({ isRented, isPaid }) => !!isRented || !!isPaid,
  );
  // slots display order left to right
  // MORNING', 'AFTERNOON', 'DAY'
  const SLOTS_ORDER = ['MORNING', 'AFTERNOON', 'DAY'];
  // because it s read only array
  const slotsCopy = [...slots];
  const orderedSlots = slotsCopy
    .map((slot) => {
      // ¯\_(ツ)_/¯
      // fix for the CC.we lose the timeslot once a slot is paid, cuz it disappears from the additionalServices route
      // and the time slot is not sent by the booking/services
      if (
        service.isPartiallyPaid &&
        slot.remainingStock === null &&
        slot.initialStock === null &&
        slot.type === 'DAY'
      ) {
        const slotsTypes = slots.map(({ type }) => type);
        // find the missing slot type ( transformed to DAY by error  )
        const missingSlot = SLOTS_ORDER.filter((s) => !slotsTypes.includes(s))?.[0] || 'DAY';
        // this is the paid slot we need to fix it's slot type
        return { ...slot, type: missingSlot };
      }

      return slot;
    })
    .sort((a, b) => {
      return SLOTS_ORDER.indexOf(a.type) - SLOTS_ORDER.indexOf(b.type);
    });

  const handleSubmit = async (values) => {
    setIsLoading(true);
    const selectedSlotProduct = slots.find((slot) => slot.id === values.selectedSlotProductId);

    if (values.selectedSlotProductId === initiallySelectedSlot?.id || !selectedSlotProduct) {
      setIsLoading(false);
      return;
    }

    addAttendeeChildcare({
      attendeeId: service.childId || '',
      pricingInfo: service,
      serviceId: selectedSlotProduct.id,
    }).finally(() => {
      setIsLoading(false);
      onClose();
    });
  };

  return (
    <Modal
      ariaLabel={headerLabel}
      closeLabel={t('common.close')}
      onClose={onClose}
      size="medium"
      className=""
    >
      <div className="align-center flex flex-col justify-center p-30">
        <h3 className="mt-10 text-center font-brand text-24 font-bold leading-1 text-black">
          {headerLabel}
        </h3>
        <div className="flex flex-col justify-center p-40 pb-0">
          <p className="py-20 text-14 font-bold">
            {t('booking.activities.childcare.chooseFullStayTimeSlot')}
          </p>
          <Formik
            initialValues={{
              selectedSlotProductId:
                orderedSlots.find((slot) => slot.id === initiallySelectedSlot?.id)?.id || '',
            }}
          >
            {(formik) => (
              <form>
                <RadioGroup
                  className="mb-10 flex-col md:flex-row"
                  name="selectedSlotProductId"
                  options={orderedSlots?.map((slot) => ({
                    value: slot.id,
                    label: (
                      <div className="flex flex-col">
                        <span className="text-14 font-bold">{getSlotLabel(slot.type)}</span>
                        <span className="text-12 font-bold text-sienna">
                          + {slot.attendees[0].price.formattedValue}
                        </span>
                        {slot.remainingStock === 0 && (
                          <span className="text-12 font-bold text-sienna">
                            {t('booking.services.childcare.childcareFull')}
                          </span>
                        )}
                        {slot.remainingStock > 0 && slot.remainingStock <= 10 && (
                          <span className="text-12 font-bold text-sienna">
                            {t('booking.services.childcare.childcareAvailabilities', {
                              count: slot.remainingStock,
                            })}
                          </span>
                        )}
                      </div>
                    ),
                    isDisabledRadio:
                      slot.remainingStock === 0 ||
                      service.attendees.find(({ id }) => id === service.childId)?.schedules?.[0]
                        ?.isPartiallyPaid,
                  }))}
                  radioHasBorder={false}
                  itemsCentered={false}
                />
                <div className="flex w-full items-center justify-center pt-40">
                  <Button
                    onClick={() => handleSubmit(formik.values)}
                    isDisabled={
                      !formik.values.selectedSlotProductId ||
                      formik.values.selectedSlotProductId === initiallySelectedSlot?.id
                    }
                    data-testid="PopInReservationBlockMorningAfternoonExclusive-add"
                    isLoading={isLoading}
                    size="medium"
                    trackingData={{}}
                  >
                    {t('booking.common.add')} +
                    {
                      orderedSlots?.find((slot) => slot.id === formik.values.selectedSlotProductId)
                        ?.attendees[0]?.price?.formattedValue
                    }
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};
